'use strict';

/**
 * @fileoverview A set whose contents are fixed at construction — and which cannot be widened by ANY
 * route (MUTATION-INSTRUMENT-001 F-TOCONTAIN-1 / -2).
 *
 * `Object.freeze(new Set(...))` does NOT stop `add` / `delete` / `clear`: a Set's entries live in an
 * internal slot, not in properties, so the freeze promises a read-only list it does not deliver. Nor
 * does a `Set` subclass that overrides the three mutators, because `Set.prototype.add.call(set, x)`
 * writes the subclass instance's internal slot directly. Exported allow-lists and deny-lists are
 * process-wide state that a gate reads, so either hole lets one importer widen the gate for everyone.
 *
 * This class is therefore NOT a `Set`. It holds its entries in a private `#entries` Set that no
 * outside code can name and exposes only the read half of the Set contract (`has`, `size`,
 * iteration, `forEach`, `keys`, `values`, `entries`). Every intrinsic Set mutator called on it meets
 * an incompatible receiver and throws. The instance, the prototype and the class are frozen, so the
 * read API cannot be replaced either, and `forEach` hands its callback this object — never the
 * backing Set. Consumers read it exactly as they read a Set: `.has()`, spread, `for…of`,
 * `new Set(x)`, `Array.from(x)`, and as the set-like argument of `Set#isSubsetOf` and friends.
 *
 * @module @puffin-core/read-only/readOnlySet
 */

class ReadOnlySet {
  /** The fixed contents. Private: reachable by no code outside this class body. */
  #entries;

  /**
   * @param {Iterable<*>} [values] - The fixed contents; copied, so later changes to the source are
   *   not seen.
   */
  constructor(values) {
    this.#entries = new Set(values);
    Object.freeze(this);
  }

  /**
   * @param {*} value
   * @returns {boolean} Whether `value` is a member.
   */
  has(value) {
    return this.#entries.has(value);
  }

  /** @returns {number} The member count. */
  get size() {
    return this.#entries.size;
  }

  /**
   * Calls `callback(value, value, this)` per member, in insertion order — the Set contract, with the
   * ReadOnlySet (never the private backing Set) as the third argument.
   *
   * @param {(value: *, key: *, set: ReadOnlySet) => void} callback
   * @param {*} [thisArg]
   * @throws {TypeError} When `callback` is not a function, as `Set#forEach` does.
   */
  forEach(callback, thisArg) {
    if (typeof callback !== 'function') {
      throw new TypeError('ReadOnlySet#forEach: callback must be a function');
    }
    for (const value of this.#entries) {
      callback.call(thisArg, value, value, this);
    }
  }

  /** @returns {Iterator<*>} The members. */
  keys() {
    return this.#entries.keys();
  }

  /** @returns {Iterator<*>} The members. */
  values() {
    return this.#entries.values();
  }

  /** @returns {Iterator<Array<*>>} `[value, value]` pairs, as `Set#entries`. */
  entries() {
    return this.#entries.entries();
  }

  /** @returns {Iterator<*>} The members. */
  [Symbol.iterator]() {
    return this.#entries.values();
  }

  /** @returns {string} `'ReadOnlySet'`, for `Object.prototype.toString`. */
  get [Symbol.toStringTag]() {
    return 'ReadOnlySet';
  }
}

Object.freeze(ReadOnlySet.prototype);
Object.freeze(ReadOnlySet);

module.exports = { ReadOnlySet };
